import { type FC, type HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

/**
 * Card component props
 */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Highlighted variant for active/selected state */
  highlighted?: boolean;
}

/**
 * Card - Container component for content grouping
 */
export const Card: FC<CardProps> = ({
  highlighted = false,
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        'bg-bg-secondary border border-border-subtle rounded shadow-default overflow-hidden',
        highlighted && 'border-accent-primary shadow-md',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * CardHeader component props
 */
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * CardHeader - Header section of card
 */
export const CardHeader: FC<CardHeaderProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        'px-4 pt-4 sm:px-5 sm:pt-5',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * CardTitle component props
 */
export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /** HTML heading level */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * CardTitle - Title element within card header
 */
export const CardTitle: FC<CardTitleProps> = ({
  as: Component = 'h3',
  className,
  children,
  ...props
}) => {
  return (
    <Component
      className={cn(
        'font-display text-lg font-bold tracking-wide text-accent-primary',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

/**
 * CardBody component props
 */
export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * CardBody - Main content area of card
 */
export const CardBody: FC<CardBodyProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div className={cn('p-4 sm:p-5', className)} {...props}>
      {children}
    </div>
  );
};

/**
 * CardFooter component props
 */
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * CardFooter - Footer section of card
 */
export const CardFooter: FC<CardFooterProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        'px-4 py-3 bg-bg-tertiary border-t border-border-subtle',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};
